import React, { Fragment, useState, useEffect, useRef } from 'react';
import { withSize } from 'react-sizeme';
import classnames from 'classnames';
import d3 from '~/core/scripts/chart-components/d3';

import useCsvData from '~/core/scripts/hooks/useCsvData';
import ChartWrapper from '~/core/scripts/chart-components/ChartWrapper';
import XAxis from '~/core/scripts/chart-components/XAxis';
import YAxisRight from '~/core/scripts/chart-components/YAxisRight';
import Legend from '~/core/scripts/components/Legend';
import Icon from '~/core/scripts/components/Icon';
import ListSearch from '~/core/scripts/components/ListSearch';
import Tooltip from '~/core/scripts/components/Tooltip';
import { numberFormat } from '~/core/scripts/utils';
import { is640Query, is768Query } from '~/core/scripts/mediaquery-helper';
import config, { colors } from '../config';

import { parseData, sortSeries, createChartData, createAreaData, sortArea, getRegistrations } from './utils';
import { getColor } from '../utils';
import LabelLayer from './LabelLayer';
import SegmentInfo from '../SegmentInfo';
import Arrow from '../Arrow';

const margin = { left: 0, top: 0, right: 40, bottom: 20 };

const BumpChart = ({ activeSegment, onAreaClick, size }) => {
  const rawData = useCsvData('neuzulassungen_co2_2010-2019.csv', ',', row => ({ ...row, value: +row.Anzahl }));
  const [hoverArea, setHoverArea] = useState(null);
  const [chartOffset, setChartOffset] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [showCta, setShowCta] = useState(true);
  const chartNode = useRef(null);
  const { width } = size;
  const height = is640Query() ? width * 0.6 : width * 0.9;

  useEffect(() => {
    if (chartNode.current) {
      const bounds = chartNode.current.getBoundingClientRect();
      setChartOffset(bounds);
    }
  }, [chartNode.current]);

  if (!rawData) {
    return null;
  }

  const data = parseData(rawData);
  const stack = d3
    .stack()
    .keys(Object.keys(data[0]).filter(key => key !== 'year' && !key.includes('_rel')))
    .offset(d3.stackOffsetNone);

  const series = stack(data);
  const registrations = getRegistrations(data, activeSegment);
  const xScale = d3
    .scaleBand()
    .domain(data.map(d => d.year))
    .range([margin.left, width - margin.right])
    .paddingOuter(0)
    .paddingInner(0.8);

  const yScale = d3
    .scaleLinear()
    .domain([0, d3.max(series, d => d3.max(d, d => d[1]))])
    .rangeRound([height - margin.bottom, margin.top]);

  const area = d3
    .area()
    .x(d => d.x)
    .y1(d => d.y)
    .y0(d => d.y1);

  sortSeries(series);

  const chartData = createChartData(series, { xScale, yScale, color: getColor });
  const areaData = createAreaData(chartData).sort(sortArea('SUV_GELAENDEWAGEN'));
  const xTicks = data.map(d => ({ value: d.year, label: d.year }));
  const options = [
    { name: 'Alle Pkw-Segmente', value: 'all' },
    ...Object.keys(config).map(key => ({ name: config[key].label, value: key }))
  ];

  const onAreaEnter = area => {
    if (area === 'SONSTIGE') {
      return false;
    }

    if (chartNode.current) {
      const bounds = chartNode.current.getBoundingClientRect();
      setChartOffset(bounds);
    }

    setHoverArea(area);
  };

  const onAreaLeave = () => {
    setHoverArea(null);
  };

  const onAreaMove = (e, area) => {
    if (!chartOffset || area === 'SONSTIGE') {
      return false;
    }

    setTooltipPos({
      x: e.clientX - chartOffset.left,
      y: e.clientY - chartOffset.top - margin.top
    });
  };

  const _onAreaClick = segment => {
    onAreaClick(segment);
    setShowCta(false);
  };

  const onSelect = option => {
    if (!option || option.value === 'all') {
      return onAreaClick(null);
    }

    setShowCta(false);
    onAreaClick(option.value);
  };

  const hasActiveSegment = activeSegment || hoverArea;
  const recentRegistration = registrations[registrations.length - 1];

  return (
    <Fragment>
      <div className="bumpchart__header viz__header">
        <h2 className="bumpchart__title viz__title">Der steile Aufstieg des SUV</h2>
        <h3 className="bumpchart__subtitle viz__subtitle">
          Jährliche Neuzulassungen in den Pkw-Segmenten, eingefärbt nach durchschnittlichem CO₂-Ausstoß, sortiert nach
          Marktanteil
        </h3>
        <Legend minLabel="besser" maxLabel="schlechter" colors={colors.map(color => ({ key: color }))} />
      </div>
      <div className="chartwrapper" ref={chartNode}>
        <ChartWrapper width={width} height={height} className="bumpchart">
          <XAxis margin={margin} ticks={xTicks} xScale={xScale} width={width} height={height} drawTickLines={false} />
          <YAxisRight
            margin={margin}
            series={series}
            yScale={yScale}
            width={width}
            height={height}
            drawTickLines={false}
            unit="%"
            data={data}
          />
          <g className="bumpchart__areas">
            {areaData.map(d => (
              <path
                key={d.key}
                d={area(d.data)}
                fill={d.color}
                className={classnames('bumpchart__area', {
                  active: hasActiveSegment && d.key === activeSegment,
                  inactive: hasActiveSegment && d.key !== activeSegment
                })}
                onMouseEnter={() => onAreaEnter(d.key)}
                onMouseLeave={() => onAreaLeave()}
                onMouseMove={e => onAreaMove(e, d.key)}
                onClick={() => (is768Query() ? null : _onAreaClick(d.key))}
              />
            ))}
          </g>
          <g className="bumpchart__bars">
            {chartData.map(currentSeries => (
              <g key={currentSeries.key}>
                {currentSeries.data.map(d => (
                  <rect
                    key={d.key}
                    x={d.x}
                    y={d.y}
                    height={d.height}
                    width={d.width}
                    fill={d.color}
                    fillOpacity={0.4}
                    className="bumpchart__rect"
                  />
                ))}
              </g>
            ))}
          </g>
          <LabelLayer xScale={xScale} yScale={yScale} rawData={rawData} series={series} />
        </ChartWrapper>
        {hoverArea && (
          <Tooltip
            pos={tooltipPos}
            data={config[hoverArea]}
            segmentName={hoverArea}
            segmentData={rawData.filter(d => d.Segment === hoverArea)}
            dataRel={[data[0][`${hoverArea}_rel`], data[data.length - 1][`${hoverArea}_rel`]]}
          />
        )}
      </div>
      <div className="registrations">
        <div className="registrations__label">
          <strong>{numberFormat(registrations[0])}</strong> Neuzulassungen
        </div>
        <Arrow />
        <div className="registrations__label">
          <strong>{numberFormat(recentRegistration)}</strong>
        </div>
      </div>
      <div className="bumpchart__select">
        <ListSearch
          placeholder="PKW Segement wählen ..."
          onSelect={onSelect}
          data={options}
          searchPresetsFieldName="name"
          fieldName="name"
          activeSegment={activeSegment}
        />
        {activeSegment && (
          <div className="bumpchart__info">
            <Tooltip
              isStatic
              isVisible
              data={config[activeSegment]}
              segmentName={activeSegment}
              segmentData={rawData.filter(d => d.Segment === activeSegment)}
              dataRel={[data[0][`${activeSegment}_rel`], data[data.length - 1][`${activeSegment}_rel`]]}
            />
          </div>
        )}
        {showCta && false && (
          <div className="bumpchart__cta">
            <div>Hier ein Segment wählen oder auf die Segmente in der Grafik klicken</div>
            <Icon type="arrow-left" />
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default withSize()(BumpChart);
